import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { CheckCircle, CreditCard, FileText, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentForm = ({ applicationId, collegeName, onPaymentComplete }) => {
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showWaiverForm, setShowWaiverForm] = useState(false);
  const [waiverReason, setWaiverReason] = useState('');
  const [waiverDetails, setWaiverDetails] = useState('');

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    loadPaymentStatus();
    // Check for return from Stripe
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [applicationId]);

  const loadPaymentStatus = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/payments/application/${applicationId}/status`,
        getAuthHeaders()
      );
      setPaymentStatus(response.data);
    } catch (error) {
      console.error('Error loading payment status:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) return;

    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/payments/checkout/status/${sessionId}`,
        getAuthHeaders()
      );
      
      if (response.data.payment_status === 'paid') {
        toast({ title: 'Payment Successful!', description: 'Your application fee has been paid.' });
        loadPaymentStatus();
        if (onPaymentComplete) onPaymentComplete();
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (response.data.status !== 'expired') {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/checkout/create`,
        {
          application_id: applicationId,
          fee_type: 'standard',
          origin_url: window.location.origin
        },
        getAuthHeaders()
      );
      
      // Redirect to Stripe
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to initiate payment',
        variant: 'destructive'
      });
      setProcessing(false);
    }
  };

  const handleFeeWaiverRequest = async () => {
    if (!waiverReason) {
      toast({ title: 'Error', description: 'Please select a waiver reason', variant: 'destructive' });
      return;
    }

    setProcessing(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/payments/fee-waiver/request`,
        {
          application_id: applicationId,
          reason: waiverReason,
          documentation_url: waiverDetails || null,
          counselor_approved: waiverReason === 'school_fee_waiver'
        },
        getAuthHeaders()
      );
      
      toast({ title: 'Fee Waiver Requested', description: 'Your request has been submitted for review.' });
      loadPaymentStatus();
      setShowWaiverForm(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to submit waiver request',
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading payment status...</div>;
  }

  // Already paid or waived
  if (paymentStatus?.payment_status === 'paid' || paymentStatus?.payment_status === 'waived') {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div>
              <h3 className="font-semibold text-green-800">
                {paymentStatus.payment_status === 'waived' ? 'Fee Waived' : 'Payment Complete'}
              </h3>
              <p className="text-sm text-green-600">
                {paymentStatus.payment_status === 'waived'
                  ? 'Your application fee has been waived.'
                  : `$${paymentStatus.payment_amount} paid for ${collegeName}`}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Waiver pending
  if (paymentStatus?.waiver_status === 'pending') {
    return (
      <Card className="border-yellow-200 bg-yellow-50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-8 w-8 text-yellow-600" />
            <div>
              <h3 className="font-semibold text-yellow-800">Fee Waiver Pending</h3>
              <p className="text-sm text-yellow-600">Your fee waiver request is being reviewed.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Application Fee
          </CardTitle>
          <CardDescription>
            Pay the application fee for {collegeName || 'this college'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-semibold">Standard Application Fee</p>
              <p className="text-sm text-gray-600">One-time fee per college</p>
            </div>
            <p className="text-2xl font-bold">${paymentStatus?.standard_fee || 75}</p>
          </div>

          <Button 
            onClick={handlePayment} 
            disabled={processing}
            className="w-full"
            size="lg"
          >
            {processing ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
            ) : (
              <><CreditCard className="h-4 w-4 mr-2" /> Pay ${paymentStatus?.standard_fee || 75}</>
            )}
          </Button>

          <div className="text-center">
            <button
              onClick={() => setShowWaiverForm(!showWaiverForm)}
              className="text-sm text-blue-600 hover:underline"
            >
              <FileText className="h-4 w-4 inline mr-1" />
              Request Fee Waiver
            </button>
          </div>
        </CardContent>
      </Card>

      {showWaiverForm && (
        <Card>
          <CardHeader>
            <CardTitle>Fee Waiver Request</CardTitle>
            <CardDescription>
              Select your eligibility reason for a fee waiver
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <RadioGroup value={waiverReason} onValueChange={setWaiverReason}>
              <div className="flex items-start space-x-3 p-3 border rounded-lg">
                <RadioGroupItem value="financial_need" id="financial_need" />
                <div>
                  <Label htmlFor="financial_need" className="font-medium">Financial Need</Label>
                  <p className="text-sm text-gray-600">I qualify for free/reduced lunch or similar programs</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 border rounded-lg">
                <RadioGroupItem value="school_fee_waiver" id="school_fee_waiver" />
                <div>
                  <Label htmlFor="school_fee_waiver" className="font-medium">School Counselor Approval</Label>
                  <p className="text-sm text-gray-600">My school counselor has approved a fee waiver</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 border rounded-lg">
                <RadioGroupItem value="college_board_fee_waiver" id="college_board" />
                <div>
                  <Label htmlFor="college_board" className="font-medium">College Board Fee Waiver</Label>
                  <p className="text-sm text-gray-600">I have a College Board/SAT fee waiver</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 border rounded-lg">
                <RadioGroupItem value="other" id="other" />
                <div>
                  <Label htmlFor="other" className="font-medium">Other Circumstances</Label>
                  <p className="text-sm text-gray-600">Other financial hardship (please explain below)</p>
                </div>
              </div>
            </RadioGroup>

            {waiverReason === 'other' && (
              <div>
                <Label>Please explain your circumstances</Label>
                <Textarea
                  value={waiverDetails}
                  onChange={(e) => setWaiverDetails(e.target.value)}
                  placeholder="Briefly describe your situation..."
                  rows={3}
                />
              </div>
            )}

            <Button
              onClick={handleFeeWaiverRequest}
              disabled={processing || !waiverReason}
              className="w-full"
            >
              {processing ? 'Submitting...' : 'Submit Fee Waiver Request'}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PaymentForm;
